import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Button } from '@mui/material';
import { PageHeader } from '@/components/shared';
import {
  StackCategory,
  StackProvider,
  ProviderInfo,
  STACK_CATEGORIES,
  PROVIDERS,
} from '@/types/api/ceoxStack';
import * as S from './CEOxStackSetup.styles';

type Props = {
  selections: Record<StackCategory, StackProvider>;
  onSelect: (category: StackCategory, provider: StackProvider) => void;
  onSave: () => void;
  isSaving: boolean;
  isEdit?: boolean;
};

const BADGE_LABELS: Record<string, string> = {
  available: 'Ready',
  coming_soon: 'Coming Soon',
  manual: 'Skip',
};

const CEOxStackSetupComponent = function ({
  selections,
  onSelect,
  onSave,
  isSaving,
  isEdit,
}: Props): JSX.Element {
  const renderProviderCard = (provider: ProviderInfo, category: StackCategory): JSX.Element => {
    const isSelected = selections[category] === provider.id;
    const isDisabled = provider.status === 'coming_soon';

    return (
      <S.ProviderCard
        key={provider.id}
        type="button"
        $selected={isSelected}
        $disabled={isDisabled}
        onClick={() => !isDisabled && onSelect(category, provider.id)}
      >
        {isSelected ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#054AD9', flexShrink: 0 }} />
        ) : (
          <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: '#D4D4D8', flexShrink: 0 }} />
        )}
        <S.ProviderInfo>
          <S.ProviderName>{provider.label}</S.ProviderName>
          <S.ProviderDesc>{provider.description}</S.ProviderDesc>
        </S.ProviderInfo>
        <S.Badge $variant={provider.status}>
          {BADGE_LABELS[provider.status]}
        </S.Badge>
      </S.ProviderCard>
    );
  };

  return (
    <S.Wrapper>
      <PageHeader title={isEdit ? 'Edit Business Stack' : 'Set Up Your Business Stack'} />

      <S.Intro>
        {isEdit
          ? 'Update which tools your organization uses. The CEOx dashboard will adapt to show the right integrations.'
          : 'Tell us which tools your business uses. CEOx will customize your dashboard to show the right metrics and integrations.'}
      </S.Intro>

      {STACK_CATEGORIES.map((cat) => (
        <S.CategorySection key={cat.key}>
          <S.CategoryHeader>
            <S.CategoryLabel>{cat.label}</S.CategoryLabel>
            <S.CategoryDesc>{cat.description}</S.CategoryDesc>
          </S.CategoryHeader>
          <S.ProviderGrid>
            {PROVIDERS[cat.key].map((p) => renderProviderCard(p, cat.key))}
          </S.ProviderGrid>
        </S.CategorySection>
      ))}

      <S.Footer>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaving}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 4,
          }}
        >
          {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Get Started'}
        </Button>
      </S.Footer>
    </S.Wrapper>
  );
};

export default CEOxStackSetupComponent;
